/**
 * Adicional de frete da proposta (mode=orcamento): o fornecedor soma um valor ao frete que o sistema já calcula
 * (faixa/região + pedágio). Nunca substitui nem descarta o frete calculado: o cliente vê UM só valor de frete
 * (calculado + adicional) e segue a compra normalmente.
 *
 * O adicional vai junto com os itens do carrinho (`freightExtra`) e com o pedido (events.freight_extra): assim o
 * carrinho do cliente, ao recalcular o frete, não perde o adicional, e uma proposta nova não herda o da anterior.
 */
export const MAX_FREIGHT_EXTRA = 100000;

export const round2 = (n: number): number => Math.round(n * 100) / 100;

/** "50", "50,5", "1.250,00", 50 -> número >= 0 com 2 casas; vazio/negativo/inválido vira 0. */
export const parseFreightExtra = (raw: unknown): number => {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? Math.min(round2(raw), MAX_FREIGHT_EXTRA) : 0;
  if (String(raw ?? '').trim().startsWith('-')) return 0;
  const cleaned = String(raw ?? '').replace(/[^\d.,]/g, '');
  if (!cleaned) return 0;
  const normalized = cleaned.includes(',') ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned;
  const n = Number(normalized);
  return Number.isFinite(n) && n > 0 ? Math.min(round2(n), MAX_FREIGHT_EXTRA) : 0;
};

type WithFreightExtra = { freightExtra?: number | null };

/** Adicional vigente no carrinho (qualquer item que o carregue). */
export const cartFreightExtra = <T extends WithFreightExtra>(items: T[] | null | undefined): number => {
  for (const item of items ?? []) {
    const n = parseFreightExtra(item?.freightExtra);
    if (n > 0) return n;
  }
  return 0;
};

/** O fornecedor já mexeu no campo (permite gravar 0 para remover um adicional que já estava no pedido). */
export const freightExtraTouched = <T extends WithFreightExtra>(items: T[] | null | undefined): boolean =>
  (items ?? []).some((item) => item?.freightExtra !== undefined && item?.freightExtra !== null);

/** Aplica o adicional a todos os itens (vazio/0 remove, mas marca que o fornecedor mexeu). */
export const withFreightExtra = <T extends WithFreightExtra>(items: T[], extra: number): T[] =>
  items.map((item) => ({ ...item, freightExtra: parseFreightExtra(extra) }));

/** Itens sem o adicional quando existe em outros (item adicionado depois) recebem o mesmo valor. */
export const normalizeFreightExtra = <T extends WithFreightExtra>(items: T[]): T[] => {
  const extra = cartFreightExtra(items);
  if (extra <= 0) return items;
  return items.some((item) => parseFreightExtra(item?.freightExtra) !== extra) ? withFreightExtra(items, extra) : items;
};

/** Frete único exibido e gravado: frete calculado + pedágio + adicional do fornecedor. Retirada no local não tem frete. */
export const computeDeliveryFee = (parts: { base: number; toll: number; extra: number; isPickup?: boolean }): number => {
  if (parts.isPickup) return 0;
  return round2((Number(parts.base) || 0) + (Number(parts.toll) || 0) + parseFreightExtra(parts.extra));
};

/** Colunas de events a gravar: só envia freight_extra quando há valor ou o fornecedor mexeu (antes da coluna existir nada muda). */
export const freightExtraColumns = <T extends WithFreightExtra>(items: T[] | null | undefined): Record<string, number> => {
  const extra = cartFreightExtra(items);
  return extra > 0 || freightExtraTouched(items) ? { freight_extra: extra } : {};
};

/**
 * Adicional da proposta que o cliente abriu pelo link, guardado na sessão ao carregar a proposta. O carrinho do cliente
 * pode ser remontado a partir dos itens (perdendo o campo dos itens); esta cópia garante que o frete extra continue
 * somado ao frete calculado.
 */
export const SHARED_FREIGHT_EXTRA_KEY = 'ameup_shared_freight_extra';

export const readSharedFreightExtra = (): number => {
  try {
    return parseFreightExtra(sessionStorage.getItem(SHARED_FREIGHT_EXTRA_KEY));
  } catch {
    return 0;
  }
};

export const writeSharedFreightExtra = (value: number): void => {
  try {
    const n = parseFreightExtra(value);
    if (n > 0) sessionStorage.setItem(SHARED_FREIGHT_EXTRA_KEY, String(n));
    else sessionStorage.removeItem(SHARED_FREIGHT_EXTRA_KEY);
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('ameup-shared-freight-extra'));
  } catch {
    // sem sessionStorage: vale só o campo dos itens
  }
};
